(function () {
  const MOBILE_MAX = 749;
  const POSITION_ATTRS = {
    "desktop-large": "data-desktop-large-position",
    "desktop-medium": "data-desktop-medium-position",
    "desktop-compact": "data-desktop-compact-position",
    "mobile-large": "data-mobile-large-position",
    "mobile-medium": "data-mobile-medium-position"
  };

  function getLayoutKey(productList) {
    const isMobile = window.matchMedia(`(max-width: ${MOBILE_MAX}px)`).matches;
    const device = isMobile ? "mobile" : "desktop";
    const mode = productList.getAttribute(`collection-${device}-layout`) || "large";
    return `${device}-${mode}`;
  }

  function getColumns(layoutKey, productList) {
    if (layoutKey === "mobile-large") return 1;
    if (layoutKey === "mobile-medium") return 2;

    const section = productList.closest("[id^='shopify-section-']");
    if (section) {
      const styles = getComputedStyle(section);
      const varName =
        layoutKey === "desktop-compact"
          ? "--collection-items-per-row-compact"
          : layoutKey === "desktop-medium"
            ? "--collection-items-per-row-medium"
            : "--collection-items-per-row-large";
      const parsed = parseInt(styles.getPropertyValue(varName), 10);
      if (!Number.isNaN(parsed) && parsed > 0) return parsed;
    }

    if (layoutKey === "desktop-compact") return 5;
    if (layoutKey === "desktop-medium") return 4;
    return 3;
  }

  function isMobileMedium(layoutKey) {
    return layoutKey === "mobile-medium";
  }

  function isMobileLarge(layoutKey) {
    return layoutKey === "mobile-large";
  }

  function getBannerSpace(bannerEl) {
    const raw = (bannerEl.getAttribute("data-grid-space") || "").trim();
    if (raw === "1") return "1x1";
    if (raw === "2") return "2x2";
    return "full";
  }

  function getDesiredPosition(bannerEl, layoutKey) {
    const attr = POSITION_ATTRS[layoutKey];
    if (!attr) return 0;
    const value = parseInt(bannerEl.getAttribute(attr), 10);
    return Number.isFinite(value) && value > 0 ? value : 0;
  }

  function footprintForSpace(space, layoutKey, cols, slot) {
    if (space === "1x1") return { rowSpan: 1, colSpan: 1 };
    if (space === "2x2") {
      if (isMobileLarge(layoutKey)) return { rowSpan: 2, colSpan: 1 };
      if (isMobileMedium(layoutKey)) return { rowSpan: 2, colSpan: 2 };
      return { rowSpan: 2, colSpan: 2 };
    }
    if (isMobileMedium(layoutKey) || isMobileLarge(layoutKey)) return { rowSpan: 1, colSpan: 1 };
    const startCol = (slot - 1) % cols;
    return { rowSpan: 1, colSpan: cols - startCol };
  }

  function slotsForBanner(space, layoutKey, cols, slot) {
    if (space === "1x1") return 1;
    if (space === "2x2") {
      if (isMobileLarge(layoutKey)) return 2;
      return 4;
    }
    if (isMobileMedium(layoutKey)) return 2;
    return cols - ((slot - 1) % cols);
  }

  function createUniformSimulator(cols) {
    const occupied = new Set();
    let slot = 1;

    return {
      addProduct: function () {
        const startCol = (slot - 1) % cols;
        const startRow = Math.floor((slot - 1) / cols);
        occupied.add(`${startRow},${startCol}`);
        slot += 1;
      },
      canPlace: function (space, layoutKey) {
        const footprint = footprintForSpace(space, layoutKey, cols, slot);
        const startCol = (slot - 1) % cols;
        const startRow = Math.floor((slot - 1) / cols);
        if (startCol + footprint.colSpan > cols) return false;
        for (let r = 0; r < footprint.rowSpan; r++) {
          for (let c = 0; c < footprint.colSpan; c++) {
            if (occupied.has(`${startRow + r},${startCol + c}`)) return false;
          }
        }
        return true;
      },
      addBanner: function (space, layoutKey) {
        const footprint = footprintForSpace(space, layoutKey, cols, slot);
        const startCol = (slot - 1) % cols;
        const startRow = Math.floor((slot - 1) / cols);
        for (let r = 0; r < footprint.rowSpan; r++) {
          for (let c = 0; c < footprint.colSpan; c++) {
            occupied.add(`${startRow + r},${startCol + c}`);
          }
        }
        if (space === "2x2") slot += slotsForBanner(space, layoutKey, cols, slot);
        else if (space === "full") slot += slotsForBanner(space, layoutKey, cols, slot);
        else slot += 1;
      }
    };
  }

  function createMobileMediumSimulator() {
    const occupied = new Set();
    let row = 0;
    let col = 0;
    let streamOrdinal = 0;

    function isFree(r, c, rowSpan, colSpan) {
      for (let y = 0; y < rowSpan; y++) {
        for (let x = 0; x < colSpan; x++) {
          if (c + x >= 2) return false;
          if (occupied.has(`${r + y},${c + x}`)) return false;
        }
      }
      return true;
    }

    function mark(r, c, rowSpan, colSpan) {
      for (let y = 0; y < rowSpan; y++) {
        for (let x = 0; x < colSpan; x++) {
          occupied.add(`${r + y},${c + x}`);
        }
      }
    }

    function advanceCursor(rowSpan, colSpan) {
      mark(row, col, rowSpan, colSpan);
      if (colSpan === 2) {
        row += rowSpan;
        col = 0;
        return;
      }
      if (col === 0) col = 1;
      else {
        row += rowSpan;
        col = 0;
      }
    }

    function addStreamItem() {
      streamOrdinal += 1;
      const isFull = (streamOrdinal - 1) % 3 === 0;
      advanceCursor(1, isFull ? 2 : 1);
    }

    function canPlaceStreamItem() {
      const nextOrdinal = streamOrdinal + 1;
      const isFull = (nextOrdinal - 1) % 3 === 0;
      return isFree(row, col, 1, isFull ? 2 : 1);
    }

    return {
      addProduct: addStreamItem,
      canPlace: function () {
        return canPlaceStreamItem();
      },
      addBanner: addStreamItem
    };
  }

  function createSimulator(layoutKey, cols) {
    if (isMobileMedium(layoutKey)) return createMobileMediumSimulator();
    return createUniformSimulator(cols);
  }

  function buildMergedStream(products, bannerClones, layoutKey, cols) {
    const pending = bannerClones
      .map(function (banner, order) {
        return {
          banner: banner,
          order: order,
          desired: getDesiredPosition(banner, layoutKey),
          space: getBannerSpace(banner),
          placed: false
        };
      })
      .filter(function (entry) {
        return entry.desired > 0;
      })
      .sort(function (a, b) {
        if (a.desired !== b.desired) return a.desired - b.desired;
        return a.order - b.order;
      });

    const stream = [];
    const simulator = createSimulator(layoutKey, cols);
    let productIndex = 0;
    let bannerIndex = 0;

    while (productIndex < products.length || bannerIndex < pending.length) {
      const nextBanner = pending[bannerIndex];

      if (nextBanner && !nextBanner.placed) {
        while (productIndex < products.length && productIndex + 1 < nextBanner.desired) {
          simulator.addProduct();
          stream.push({ type: "product", node: products[productIndex] });
          productIndex += 1;
        }

        let placed = false;
        for (let attempt = 0; attempt < 120; attempt++) {
          const canPlace = isMobileMedium(layoutKey)
            ? simulator.canPlace()
            : simulator.canPlace(nextBanner.space, layoutKey);

          if (canPlace) {
            if (isMobileMedium(layoutKey)) {
              simulator.addBanner();
            } else {
              simulator.addBanner(nextBanner.space, layoutKey);
            }
            stream.push({ type: "banner", banner: nextBanner.banner });
            nextBanner.placed = true;
            bannerIndex += 1;
            placed = true;
            break;
          }
          if (productIndex >= products.length) break;
          simulator.addProduct();
          stream.push({ type: "product", node: products[productIndex] });
          productIndex += 1;
        }

        if (!placed) {
          nextBanner.placed = true;
          bannerIndex += 1;
        }
        continue;
      }

      if (productIndex < products.length) {
        simulator.addProduct();
        stream.push({ type: "product", node: products[productIndex] });
        productIndex += 1;
        continue;
      }

      break;
    }

    return stream;
  }

  function getVirtualizedScroll(productList) {
    return productList.parentElement.querySelector("virtualized-scroll");
  }

  function syncMobileMediumLayout(productList) {
    if (getLayoutKey(productList) !== "mobile-medium") return;

    let streamIndex = 0;
    Array.from(productList.children).forEach(function (child) {
      if (!child.matches("product-card") && !child.matches("collection-promo-banner")) return;

      streamIndex += 1;
      const isFullRow = (streamIndex - 1) % 3 === 0;

      if (isFullRow) {
        child.style.gridColumn = "1 / -1";
      } else {
        child.style.removeProperty("grid-column");
      }

      child.style.removeProperty("grid-row");
    });
  }

  function nodesInSameOrder(container, orderedNodes) {
    const current = container.children;
    if (current.length !== orderedNodes.length) return false;
    for (let i = 0; i < orderedNodes.length; i++) {
      if (current[i] !== orderedNodes[i]) return false;
    }
    return true;
  }

  function applyStream(productList, stream) {
    const orderedNodes = stream.map(function (item) {
      return item.type === "product" ? item.node : item.banner;
    });

    if (nodesInSameOrder(productList, orderedNodes)) {
      syncMobileMediumLayout(productList);
      return;
    }

    const scroller = getVirtualizedScroll(productList);

    if (scroller && typeof scroller.reorderChildren === "function") {
      scroller.reorderChildren(orderedNodes);
    } else {
      if (scroller && typeof scroller.prepareForExternalReorder === "function") {
        scroller.prepareForExternalReorder();
      }

      const fragment = document.createDocumentFragment();
      orderedNodes.forEach(function (node) {
        fragment.appendChild(node);
      });
      productList.replaceChildren(fragment);

      if (scroller && typeof scroller.afterExternalReorder === "function") {
        scroller.afterExternalReorder();
      }
    }

    syncMobileMediumLayout(productList);
  }

  function collectProducts(productList) {
    return Array.from(productList.children).filter(function (child) {
      return child.matches("product-card");
    });
  }

  function collectBannerTemplates(root) {
    const templates = root.querySelector("[data-collection-promo-templates]");
    if (!templates) return [];
    return Array.from(templates.content.children).filter(function (child) {
      return child.matches("collection-promo-banner");
    });
  }

  function resolveBannerInstances(productList, templates) {
    const existingById = new Map();
    productList.querySelectorAll("collection-promo-banner[data-promo-id]").forEach(function (banner) {
      existingById.set(banner.getAttribute("data-promo-id"), banner);
    });

    return templates.map(function (template) {
      const id = template.getAttribute("data-promo-id");
      return existingById.get(id) || template.cloneNode(true);
    });
  }

  function integrateProductList(productList, root) {
    if (!productList) return;

    const sectionRoot = root || productList.closest("[id^='shopify-section-']") || document;
    const templates = collectBannerTemplates(sectionRoot);
    if (!templates.length) return;

    const layoutKey = getLayoutKey(productList);
    const cols = getColumns(layoutKey, productList);
    const products = collectProducts(productList);
    const bannerInstances = resolveBannerInstances(productList, templates);
    const stream = buildMergedStream(products, bannerInstances, layoutKey, cols);
    applyStream(productList, stream);
  }

  function mergeFetchedCards(productList, newCards, root) {
    if (!productList || !newCards.length) return newCards;

    const sectionRoot = root || productList.closest("[id^='shopify-section-']") || document;
    const templates = collectBannerTemplates(sectionRoot);
    if (!templates.length) return newCards;

    const existingProducts = collectProducts(productList);
    const incomingProducts = newCards.filter(function (node) {
      return node.matches("product-card");
    });
    const allProducts = existingProducts.concat(incomingProducts);

    const layoutKey = getLayoutKey(productList);
    const cols = getColumns(layoutKey, productList);
    const bannerInstances = resolveBannerInstances(productList, templates);
    const stream = buildMergedStream(allProducts, bannerInstances, layoutKey, cols);
    applyStream(productList, stream);

    return [];
  }

  window.CollectionPromoBanners = {
    integrateProductList: integrateProductList,
    mergeFetchedCards: mergeFetchedCards,
    getLayoutKey: getLayoutKey
  };

  var resizeTimer = null;
  window.addEventListener("resize", function () {
    if (resizeTimer) clearTimeout(resizeTimer);
    resizeTimer = setTimeout(function () {
      document.querySelectorAll("[data-collection-promo-templates]").forEach(function (template) {
        var section = template.closest("[id^='shopify-section-']");
        var productList = section && section.querySelector("product-list");
        if (productList) integrateProductList(productList, section);
      });
    }, 200);
  });
})();
